import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SetupMeetingModal = ({ isOpen, onClose, partnerName = "[Partner Name]", partnerEmail = "", onStepComplete }) => {
  const calendlyRef = useRef(null);
  const iframeRef = useRef(null);
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const [meetingScheduled, setMeetingScheduled] = useState(false);
  const [calendlyError, setCalendlyError] = useState(false);
  const isInitializingRef = useRef(false);

  // Calendly link - Replace with your actual Calendly link
  // Format: your-username/meeting-type OR https://calendly.com/your-username/meeting-type
  // You can get this from your Calendly account settings
  const CALENDLY_LINK_RAW = process.env.REACT_APP_CALENDLY_LINK || 'rumi-sabinayasmin/30min';
  
  // Extract just the path part if a full URL is provided
  const getCalendlyPath = (link) => {
    if (!link) return '';
    // If it's a full URL, extract the path
    if (link.startsWith('https://calendly.com/')) {
      return link.replace('https://calendly.com/', '');
    }
    if (link.startsWith('http://calendly.com/')) {
      return link.replace('http://calendly.com/', '');
    }
    if (link.startsWith('calendly.com/')) {
      return link.replace('calendly.com/', '');
    }
    // Otherwise assume it's already just the path
    return link;
  };
  
  const CALENDLY_LINK = getCalendlyPath(CALENDLY_LINK_RAW);

  const initializeCalendly = useCallback(() => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      console.log('Calendly initialization already in progress');
      return;
    }

    if (!calendlyRef.current) {
      console.error('Calendly ref element is null');
      return;
    }

    // Validate Calendly link
    if (!CALENDLY_LINK || CALENDLY_LINK.trim() === '') {
      console.error('Calendly link is empty or invalid');
      setCalendlyError(true);
      return;
    }

    isInitializingRef.current = true;

    // Clean up previous iframe if it exists
    if (iframeRef.current && calendlyRef.current && calendlyRef.current.contains(iframeRef.current)) {
      try {
        calendlyRef.current.removeChild(iframeRef.current);
      } catch (e) {
        // If removal fails, it's already gone - that's fine
        console.log('Previous iframe already removed');
      }
    }

    // Build URL with prefill parameters in query string (more reliable than inline widget)
    const calendlyUrl = `https://calendly.com/${CALENDLY_LINK}`;
    const params = new URLSearchParams();
    
    // Add prefill parameters to URL
    const trimmedName = partnerName && partnerName !== '[Partner Name]' && partnerName.trim() ? partnerName.trim() : '';
    const trimmedEmail = partnerEmail && partnerEmail.trim() ? partnerEmail.trim() : '';
    
    if (trimmedName) {
      params.append('name', trimmedName);
    }
    if (trimmedEmail) {
      params.append('email', trimmedEmail);
    }

    const fullUrl = params.toString() ? `${calendlyUrl}?${params.toString()}` : calendlyUrl;

    // Use iframe embed instead of inline widget - more reliable
    try {
      const iframe = document.createElement('iframe');
      iframe.src = fullUrl;
      iframe.frameBorder = '0';
      iframe.width = '100%';
      iframe.height = '600';
      iframe.style.border = 'none';
      iframe.style.minHeight = '600px';
      iframe.title = 'Schedule a meeting';
      // Add all necessary permissions for Calendly
      iframe.setAttribute('allow', 'camera; microphone; geolocation; payment; autoplay; encrypted-media');
      // Set permissions policy to allow payment
      iframe.setAttribute('allowpaymentrequest', 'true');
      
      iframe.onload = () => {
        setIsCalendlyLoaded(true);
        isInitializingRef.current = false;
      };
      
      iframe.onerror = () => {
        console.error('Failed to load Calendly iframe');
        setCalendlyError(true);
        isInitializingRef.current = false;
      };

      iframeRef.current = iframe;
      
      if (calendlyRef.current) {
        calendlyRef.current.appendChild(iframe);
      }
    } catch (error) {
      console.error('Error creating Calendly iframe:', error);
      setCalendlyError(true);
      isInitializingRef.current = false;
    }
  }, [partnerName, partnerEmail, CALENDLY_LINK]);


  useEffect(() => {
    if (isOpen) {
      // For iframe embed, we don't need the Calendly script
      // Just initialize the iframe directly
      const timer = setTimeout(() => {
        initializeCalendly();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initializeCalendly]);

  useEffect(() => {
    // Cleanup when modal closes
    if (!isOpen) {
      isInitializingRef.current = false;
      // Clean up iframe on close
      if (iframeRef.current && calendlyRef.current && calendlyRef.current.contains(iframeRef.current)) {
        try {
          calendlyRef.current.removeChild(iframeRef.current);
        } catch (e) {
          // If removal fails, it's already gone - that's fine
        }
      }
      iframeRef.current = null;
    }
  }, [isOpen]);

  // Listen for Calendly events from iframe
  useEffect(() => {
    if (isOpen) {
      const handleMessage = (e) => {
        // Calendly sends events from calendly.com
        if (e.origin !== 'https://calendly.com') return;
        
        if (e.data && e.data.event && e.data.event.indexOf('calendly') === 0) {
          const event = e.data.event;
          
          if (event === 'calendly.event_scheduled') {
            const eventData = e.data.payload || {};
            setMeetingScheduled(true);
            
            if (onStepComplete) {
              onStepComplete({
                step: 'meeting_request',
                completed: true,
                timestamp: new Date().toISOString(),
                partnerName: partnerName,
                partnerEmail: partnerEmail,
                calendlyEventUri: eventData.event?.uri,
                calendlyInviteeUri: eventData.invitee?.uri,
                meetingScheduled: true
              });
            }

            setTimeout(() => {
              onClose();
            }, 2000);
          }
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [isOpen, partnerName, partnerEmail, onStepComplete, onClose]);

  const handleMarkStepComplete = () => {
    if (onStepComplete) {
      onStepComplete({
        step: 'meeting_request',
        completed: true,
        timestamp: new Date().toISOString(),
        partnerName: partnerName,
        partnerEmail: partnerEmail,
        manuallyMarked: true,
        meetingScheduled: false
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 rounded-t-lg flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <Calendar className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Schedule a Meeting</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {meetingScheduled ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                <span className="text-green-800 text-lg font-semibold">Meeting scheduled successfully!</span>
              </div>
              <p className="text-gray-600">You'll receive a confirmation email shortly.</p>
            </div>
          ) : (
            <>

              {/* Calendly Inline Widget */}
              {calendlyError ? (
                <div className="flex flex-col items-center justify-center h-[600px] p-6">
                  <div className="text-center max-w-md">
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800 font-semibold mb-2">Unable to load calendar</p>
                      <p className="text-yellow-700 text-sm mb-4">
                        There was an issue loading the scheduling calendar. Please contact us directly to schedule a meeting.
                      </p>
                      <a
                        href={`mailto:salaam@halalcred.com?subject=Partnership Meeting Request&body=Hello,%0D%0A%0D%0AI would like to schedule a meeting to discuss partnership.%0D%0A%0D%0AName: ${encodeURIComponent(partnerName)}%0D%0AEmail: ${encodeURIComponent(partnerEmail)}`}
                        className="inline-block px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
                      >
                        Contact Us via Email
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full" style={{ minHeight: '600px', position: 'relative' }}>
                  <div 
                    ref={calendlyRef}
                    className="calendly-inline-widget w-full"
                    style={{ 
                      minHeight: '600px',
                      width: '100%',
                      position: 'relative'
                    }}
                  >
                    {!isCalendlyLoaded && (
                      <div className="flex items-center justify-center h-[600px] absolute inset-0">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                          <p className="text-gray-600">Loading calendar...</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!meetingScheduled && (
          <div className="border-t p-4 flex justify-between items-center">
            <Button
              type="button"
              variant="default"
              onClick={handleMarkStepComplete}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Mark Step Complete
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupMeetingModal;
